import { useEffect, useState, useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { MapPin, Navigation, Users, Calendar, Building2, User2, Loader2, AlertCircle, Filter, ChevronDown, Search } from 'lucide-react';
import { supabase } from '../lib/supabase';
import { locationManager } from '../lib/locationManager';
import { Meta } from '../components/Meta';
import { PractitionerCard } from '../components/Listings/PractitionerCard';
import { EventCard } from '../components/Events/EventCard';
import { VenueCard } from '../components/Venues/VenueCard';
import { searchAddress } from '../lib/utils/geocoding';
import type { Practitioner } from '../types/practitioners';
import type { Event } from '../types/events';
import type { Venue } from '../types/venues';
import type { Community } from '../types/communities';

type ListingType = 'all' | 'practitioners' | 'events' | 'venues' | 'communities';
const MAX_RESULTS_PER_TYPE = 120;

interface NearbyItem {
  type: 'practitioner' | 'event' | 'venue' | 'community';
  id: string;
  distance: number;
  data: Practitioner | Event | Venue | Community;
}

// Calculate distance between two points using Haversine formula (in km)
function calculateDistance(lat1: number, lon1: number, lat2: number, lon2: number): number {
  const R = 6371; // Earth's radius in km
  const dLat = (lat2 - lat1) * Math.PI / 180;
  const dLon = (lon2 - lon1) * Math.PI / 180;
  const a =
    Math.sin(dLat/2) * Math.sin(dLat/2) +
    Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) *
    Math.sin(dLon/2) * Math.sin(dLon/2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a));
  return R * c;
}

function formatDistance(km: number): string {
  if (km < 1) {
    return `${Math.round(km * 1000)}m`;
  }
  return `${km.toFixed(1)}km`;
}

export function NearMe() {
  const { t } = useTranslation();
  const [location, setLocation] = useState<GeolocationCoordinates | null>(null);
  const [locationError, setLocationError] = useState<string | null>(null);
  const [locationPermission, setLocationPermission] = useState<'granted' | 'denied' | 'prompt' | 'loading'>('loading');
  const [loading, setLoading] = useState(false);
  const [nearbyItems, setNearbyItems] = useState<NearbyItem[]>([]);
  const [filter, setFilter] = useState<ListingType>('all');
  const [radius, setRadius] = useState(50); // km
  const [showFilters, setShowFilters] = useState(true); // Always open by default
  const [addressSearch, setAddressSearch] = useState('');
  const [addressSearchResults, setAddressSearchResults] = useState<Array<{ lat: string; lon: string; display_name: string }>>([]);
  const [showAddressResults, setShowAddressResults] = useState(false);
  const [searchingAddress, setSearchingAddress] = useState(false);
  const addressSearchTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const addressResultsRef = useRef<HTMLDivElement>(null);

  // Check location permission status
  useEffect(() => {
    if ('permissions' in navigator) {
      navigator.permissions.query({ name: 'geolocation' }).then((result) => {
        setLocationPermission(result.state as 'granted' | 'denied' | 'prompt');

        result.addEventListener('change', () => {
          setLocationPermission(result.state as 'granted' | 'denied' | 'prompt');
        });
      }).catch(() => {
        setLocationPermission('prompt');
      });
    } else {
      setLocationPermission('prompt');
    }
  }, []);

  // Request location
  const requestLocation = useCallback(() => {
    setLocationError(null);
    setLocationPermission('loading');

    locationManager.getLocation({
      enableHighAccuracy: true,
      timeout: 10000,
      maximumAge: 300000
    })
      .then((coords) => {
        setLocation({
          latitude: coords.latitude,
          longitude: coords.longitude,
          altitude: null,
          accuracy: 0,
          altitudeAccuracy: null,
          heading: null,
          speed: null
        });
        setLocationPermission('granted');
      })
      .catch((error) => {
        setLocationError(error.message || 'Failed to get location. Please try again.');
        setLocationPermission('denied');
      });
  }, [t]);

  // Auto-request location if permission is granted
  useEffect(() => {
    if (locationPermission === 'granted' && !location) {
      requestLocation();
    }
  }, [locationPermission, location, requestLocation]);

  // Fetch nearby items when location is available
  useEffect(() => {
    if (!location) return;

    async function fetchNearbyItems() {
      setLoading(true);
      const items: NearbyItem[] = [];
      const lat = location.latitude;
      const lon = location.longitude;
      const latDelta = radius / 111;
      const lonDelta = radius / (111 * Math.max(Math.cos((lat * Math.PI) / 180), 0.2));
      const minLat = lat - latDelta;
      const maxLat = lat + latDelta;
      const minLon = lon - lonDelta;
      const maxLon = lon + lonDelta;

      try {
        const shouldFetchPractitioners = filter === 'all' || filter === 'practitioners';
        const shouldFetchEvents = filter === 'all' || filter === 'events';
        const shouldFetchVenues = filter === 'all' || filter === 'venues';
        const shouldFetchCommunities = filter === 'all' || filter === 'communities';

        const [practitionersRes, eventsRes, venuesRes, communitiesRes] = await Promise.all([
          shouldFetchPractitioners
            ? supabase
                .from('practitioners')
                .select(`
                  *,
                  user:users (
                    id,
                    username,
                    full_name,
                    avatar_url,
                    verified,
                    user_type,
                    subscription_status
                  )
                `)
                .eq('approval_status', 'approved')
                .eq('user.user_type', 'professional')
                .eq('user.subscription_status', 'active')
                .gte('latitude', minLat)
                .lte('latitude', maxLat)
                .gte('longitude', minLon)
                .lte('longitude', maxLon)
                .limit(MAX_RESULTS_PER_TYPE)
            : Promise.resolve({ data: [] as Practitioner[] }),
          shouldFetchEvents
            ? supabase
                .from('events')
                .select(`
                  *,
                  user:users (
                    id,
                    username,
                    full_name,
                    avatar_url,
                    user_type,
                    subscription_status
                  )
                `)
                .eq('approval_status', 'approved')
                .or('visibility.eq.public,visibility.is.null')
                .eq('user.user_type', 'professional')
                .gte('start_date', new Date().toISOString().split('T')[0])
                .gte('latitude', minLat)
                .lte('latitude', maxLat)
                .gte('longitude', minLon)
                .lte('longitude', maxLon)
                .limit(MAX_RESULTS_PER_TYPE)
            : Promise.resolve({ data: [] as Event[] }),
          shouldFetchVenues
            ? supabase
                .from('venues')
                .select(`
                  *,
                  user:user_id (
                    id,
                    username,
                    full_name,
                    avatar_url,
                    user_type,
                    subscription_status
                  )
                `)
                .eq('approval_status', 'approved')
                .eq('user.user_type', 'professional')
                .gte('latitude', minLat)
                .lte('latitude', maxLat)
                .gte('longitude', minLon)
                .lte('longitude', maxLon)
                .limit(MAX_RESULTS_PER_TYPE)
            : Promise.resolve({ data: [] as Venue[] }),
          shouldFetchCommunities
            ? supabase
                .from('communities')
                .select(`
                  *,
                  owner:users!communities_owner_id_fkey (
                    id,
                    username,
                    avatar_url,
                    verified
                  )
                `)
                .eq('type', 'public')
                .gte('latitude', minLat)
                .lte('latitude', maxLat)
                .gte('longitude', minLon)
                .lte('longitude', maxLon)
                .limit(MAX_RESULTS_PER_TYPE)
            : Promise.resolve({ data: [] as Community[] }),
        ]);

        const practitioners = practitionersRes.data || [];
        const events = eventsRes.data || [];
        const venues = venuesRes.data || [];
        const communities = communitiesRes.data || [];

        for (const p of practitioners) {
          if (p.latitude && p.longitude) {
            const distance = calculateDistance(location.latitude, location.longitude, p.latitude, p.longitude);
            if (distance <= radius) {
              items.push({ type: 'practitioner', id: p.id, distance, data: p as Practitioner });
            }
          }
        }

        for (const e of events) {
          if (e.latitude && e.longitude) {
            const distance = calculateDistance(location.latitude, location.longitude, e.latitude, e.longitude);
            if (distance <= radius) {
              items.push({ type: 'event', id: e.id, distance, data: e as Event });
            }
          }
        }

        for (const v of venues) {
          if (v.latitude && v.longitude) {
            const distance = calculateDistance(location.latitude, location.longitude, v.latitude, v.longitude);
            if (distance <= radius) {
              items.push({ type: 'venue', id: v.id, distance, data: v as Venue });
            }
          }
        }

        for (const c of communities) {
          if (c.latitude && c.longitude) {
            const distance = calculateDistance(location.latitude, location.longitude, c.latitude, c.longitude);
            if (distance <= radius) {
              items.push({ type: 'community', id: c.id, distance, data: c as Community });
            }
          }
        }

        // Sort by distance
        items.sort((a, b) => a.distance - b.distance);
        setNearbyItems(items);
      } catch (error) {
        console.error('Error fetching nearby items:', error);
      } finally {
        setLoading(false);
      }
    }

    fetchNearbyItems();
  }, [location, filter, radius]);

  const filterOptions: { value: ListingType; label: string; icon: typeof MapPin }[] = [
    { value: 'all', label: t('nearMe.filterAll'), icon: MapPin },
    { value: 'practitioners', label: t('nearMe.filterPractitioners'), icon: User2 },
    { value: 'events', label: t('nearMe.filterEvents'), icon: Calendar },
    { value: 'venues', label: t('nearMe.filterVenues'), icon: Building2 },
    { value: 'communities', label: t('nearMe.filterCommunities'), icon: Users },
  ];

  const radiusOptions = [10, 25, 50, 100, 200];

  // Handle address search
  const handleAddressSearch = useCallback(async (query: string) => {
    setAddressSearch(query);
    
    if (query.length < 3) {
      setAddressSearchResults([]);
      setShowAddressResults(false);
      return;
    }

    if (addressSearchTimeoutRef.current) {
      clearTimeout(addressSearchTimeoutRef.current);
    }

    addressSearchTimeoutRef.current = setTimeout(async () => {
      setSearchingAddress(true);
      const results = await searchAddress(query);
      setAddressSearchResults(results);
      setShowAddressResults(results.length > 0);
      setSearchingAddress(false);
    }, 500);
  }, []);

  // Handle address selection
  const handleAddressSelect = useCallback((result: { lat: string; lon: string; display_name: string }) => {
    const lat = parseFloat(result.lat);
    const lon = parseFloat(result.lon);
    setLocation({ latitude: lat, longitude: lon } as GeolocationCoordinates);
    setAddressSearch(result.display_name);
    setShowAddressResults(false);
    setAddressSearchResults([]);
  }, []);

  // Close address results when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (addressResultsRef.current && !addressResultsRef.current.contains(event.target as Node)) {
        setShowAddressResults(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Render permission request screen
  if (locationPermission === 'prompt' || locationPermission === 'loading') {
    return (
      <div className="min-h-screen bg-gray-50">
        <Meta
          title={t('nearMe.metaTitle')}
          description={t('nearMe.metaDescription')}
        />

        <div className="max-w-lg mx-auto px-4 py-16 text-center">
          <div className="bg-white rounded-2xl shadow-sm p-8">
            <div className="w-20 h-20 bg-accent-base/20 rounded-full flex items-center justify-center mx-auto mb-6">
              <MapPin className="h-10 w-10 text-accent-text" />
            </div>

            <h1 className="text-2xl font-bold text-content mb-4">
              {t('nearMe.title')}
            </h1>

            <p className="text-content/70 mb-8">
              {t('nearMe.description')}
            </p>

            <button
              onClick={requestLocation}
              disabled={locationPermission === 'loading'}
              className="w-full flex items-center justify-center gap-2 px-6 py-4 bg-accent-text text-white rounded-xl font-medium hover:bg-accent-text/90 transition-colors disabled:opacity-50"
            >
              {locationPermission === 'loading' ? (
                <>
                  <Loader2 className="h-5 w-5 animate-spin" />
                  {t('nearMe.gettingLocation')}
                </>
              ) : (
                <>
                  <Navigation className="h-5 w-5" />
                  {t('nearMe.enableLocation')}
                </>
              )}
            </button>

            <p className="text-sm text-content/50 mt-4">
              {t('nearMe.locationPrivacy')}
            </p>
          </div>
        </div>
      </div>
    );
  }

  // Render error screen
  if (locationPermission === 'denied' || locationError) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Meta
          title={t('nearMe.metaTitle')}
          description={t('nearMe.metaDescription')}
        />

        <div className="max-w-lg mx-auto px-4 py-16 text-center">
          <div className="bg-white rounded-2xl shadow-sm p-8">
            <div className="w-20 h-20 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <AlertCircle className="h-10 w-10 text-red-500" />
            </div>

            <h1 className="text-2xl font-bold text-content mb-4">
              {t('nearMe.locationAccessRequired')}
            </h1>

            <p className="text-content/70 mb-8">
              {locationError || t('nearMe.locationAccessFallback')}
            </p>

            <button
              onClick={requestLocation}
              className="w-full flex items-center justify-center gap-2 px-6 py-4 bg-accent-text text-white rounded-xl font-medium hover:bg-accent-text/90 transition-colors"
            >
              <Navigation className="h-5 w-5" />
              {t('nearMe.tryAgain')}
            </button>

            <div className="mt-6 pt-6 border-t border-gray-100">
              <p className="text-sm text-content/60 mb-4">
                {t('nearMe.browseByCategory')}
              </p>
              <div className="flex flex-wrap justify-center gap-2">
                <Link to="/practitioners" className="px-4 py-2 bg-accent-base/10 text-accent-text rounded-full text-sm hover:bg-accent-base/20">
                  {t('nearMe.filterPractitioners')}
                </Link>
                <Link to="/events" className="px-4 py-2 bg-accent-base/10 text-accent-text rounded-full text-sm hover:bg-accent-base/20">
                  {t('nearMe.filterEvents')}
                </Link>
                <Link to="/venues" className="px-4 py-2 bg-accent-base/10 text-accent-text rounded-full text-sm hover:bg-accent-base/20">
                  {t('nearMe.filterVenues')}
                </Link>
                <Link to="/communities" className="px-4 py-2 bg-accent-base/10 text-accent-text rounded-full text-sm hover:bg-accent-base/20">
                  {t('nearMe.filterCommunities')}
                </Link>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Render main content
  return (
    <div className="min-h-screen bg-gray-50 pb-20">
      <Meta
        title={t('nearMe.metaTitle')}
        description={t('nearMe.metaDescription')}
      />

      {/* Header */}
      <div className="bg-white border-b border-gray-100 sticky top-0 z-20">
        <div className="max-w-page mx-auto px-4 py-4 space-y-4">
          <div className="flex items-center justify-between">
            <h1 className="text-xl font-bold text-content flex items-center gap-2">
              <MapPin className="h-5 w-5 text-accent-text" />
              {t('nearMe.title')}
            </h1>

            <button
              onClick={() => setShowFilters(!showFilters)}
              className="flex items-center gap-1 px-3 py-2 text-sm bg-accent-base/10 text-accent-text rounded-lg"
            >
              <Filter className="h-4 w-4" />
              {t('nearMe.filtersButton')}
              <ChevronDown className={`h-4 w-4 transition-transform ${showFilters ? 'rotate-180' : ''}`} />
            </button>
          </div>

          {/* Expanded Filters - NOW ABOVE EVERYTHING */}
          {showFilters && (
            <div className="bg-white rounded-xl p-4 space-y-4 shadow-sm border border-gray-100 relative z-10">
              {/* Address Search */}
              <div className="relative" ref={addressResultsRef}>
                <label className="block text-sm font-medium text-content/70 mb-2">
                  {t('nearMe.searchByAddress')}
                </label>
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-content/40" />
                  <input
                    type="text"
                    value={addressSearch}
                    onChange={(e) => handleAddressSearch(e.target.value)}
                    placeholder={t('nearMe.addressPlaceholder')}
                    className="w-full pl-10 pr-4 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-accent-text focus:border-transparent"
                  />
                  {searchingAddress && (
                    <Loader2 className="absolute right-3 top-1/2 transform -translate-y-1/2 h-4 w-4 animate-spin text-content/40" />
                  )}
                </div>
                {showAddressResults && addressSearchResults.length > 0 && (
                  <div className="absolute z-10 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-60 overflow-y-auto">
                    {addressSearchResults.map((result, idx) => (
                      <button
                        key={idx}
                        onClick={() => handleAddressSelect(result)}
                        className="w-full text-left px-4 py-2 hover:bg-gray-50 text-sm text-content border-b border-gray-100 last:border-b-0"
                      >
                        {result.display_name}
                      </button>
                    ))}
                  </div>
                )}
              </div>

              {/* Search Radius */}
              <div className="flex flex-col sm:flex-row sm:items-center gap-4">
                <span className="text-sm text-content/70">{t('nearMe.searchRadius')}</span>
                <div className="flex gap-2">
                  {radiusOptions.map((r) => (
                    <button
                      key={r}
                      onClick={() => setRadius(r)}
                      className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                        radius === r
                          ? 'bg-accent-text text-white'
                          : 'bg-gray-100 text-content/70 hover:bg-gray-200'
                      }`}
                    >
                      {r}{t('nearMe.km')}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* Filter Chips */}
          <div className="flex gap-2 overflow-x-auto pb-2 -mx-4 px-4 scrollbar-hide">
            {filterOptions.map((option) => {
              const Icon = option.icon;
              const count = filter === option.value || option.value === 'all'
                ? nearbyItems.filter(item => option.value === 'all' || item.type === option.value.slice(0, -1)).length
                : 0;

              return (
                <button
                  key={option.value}
                  onClick={() => setFilter(option.value)}
                  className={`flex items-center gap-1.5 px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-colors ${
                    filter === option.value
                      ? 'bg-accent-text text-white'
                      : 'bg-white text-content/70 border border-gray-200 hover:bg-gray-50'
                  }`}
                >
                  <Icon className="h-4 w-4" />
                  {option.label}
                  {location && !loading && (
                    <span className={`ml-1 px-1.5 py-0.5 rounded-full text-xs ${
                      filter === option.value ? 'bg-white/20' : 'bg-gray-100'
                    }`}>
                      {option.value === 'all'
                        ? nearbyItems.length
                        : nearbyItems.filter(item => item.type === option.value.slice(0, -1)).length}
                    </span>
                  )}
                </button>
              );
            })}
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-page mx-auto px-4 py-6">
        {loading ? (
          <div className="flex flex-col items-center justify-center py-16">
            <Loader2 className="h-8 w-8 text-accent-text animate-spin mb-4" />
            <p className="text-content/70">{t('nearMe.findingNearby')}</p>
          </div>
        ) : nearbyItems.length === 0 ? (
          <div className="bg-white rounded-xl p-8 text-center">
            <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <MapPin className="h-8 w-8 text-content/40" />
            </div>
            <h2 className="text-lg font-semibold text-content mb-2">
              {t('nearMe.nothingFound')}
            </h2>
            <p className="text-content/60 mb-6">
              {t('nearMe.nothingFoundMessage', { filter: filter === 'all' ? 'listings' : filter, radius })}
            </p>
            <div className="flex flex-wrap justify-center gap-2">
              <Link to="/practitioners" className="px-4 py-2 bg-accent-text text-white rounded-lg text-sm hover:bg-accent-text/90">
                {t('nearMe.browseAllPractitioners')}
              </Link>
              <Link to="/events" className="px-4 py-2 bg-accent-base/10 text-accent-text rounded-lg text-sm hover:bg-accent-base/20">
                {t('nearMe.browseAllEvents')}
              </Link>
            </div>
          </div>
        ) : (
          <div className="space-y-6">
            {/* Results count */}
            <p className="text-sm text-content/60">
              {t('nearMe.foundResults', { count: nearbyItems.length, filter: filter === 'all' ? 'listings' : filter, radius })}
            </p>

            {/* Results grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {nearbyItems.map((item) => (
                <div key={`${item.type}-${item.id}`} className="relative">
                  {/* Distance badge */}
                  <div className="absolute top-3 left-3 z-10 flex items-center gap-1 px-2 py-1 bg-white/90 backdrop-blur-sm rounded-full text-xs font-medium text-content shadow-sm">
                    <MapPin className="h-3 w-3 text-accent-text" />
                    {formatDistance(item.distance)}
                  </div>

                  {item.type === 'practitioner' && (
                    <PractitionerCard practitioner={item.data as Practitioner} />
                  )}

                  {item.type === 'event' && (
                    <EventCard event={item.data as Event} />
                  )}

                  {item.type === 'venue' && (
                    <VenueCard venue={item.data as Venue} />
                  )}

                  {item.type === 'community' && (
                    <CommunityCard community={item.data as Community} distance={item.distance} />
                  )}
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// Simple community card component
function CommunityCard({ community, distance }: { community: Community; distance: number }) {
  const { t } = useTranslation();
  return (
    <Link
      to={`/communities/${community.id}`}
      className="block bg-white rounded-xl shadow-sm overflow-hidden hover:shadow-md transition-shadow"
    >
      <div className="relative h-32">
        {community.banner_url ? (
          <img
            src={community.banner_url}
            alt={community.name}
            className="w-full h-full object-cover"
          />
        ) : (
          <div className="w-full h-full bg-gradient-to-br from-accent-base/30 to-accent-text/20" />
        )}

        <div className="absolute -bottom-6 left-4">
          {community.avatar_url ? (
            <img
              src={community.avatar_url}
              alt={community.name}
              className="w-12 h-12 rounded-full border-2 border-white object-cover"
            />
          ) : (
            <div className="w-12 h-12 rounded-full border-2 border-white bg-accent-base flex items-center justify-center">
              <Users className="h-6 w-6 text-accent-text" />
            </div>
          )}
        </div>
      </div>

      <div className="p-4 pt-8">
        <h3 className="font-semibold text-content line-clamp-1">{community.name}</h3>
        {community.description && (
          <p className="text-sm text-content/60 mt-1 line-clamp-2">{community.description}</p>
        )}
        <div className="flex items-center gap-2 mt-3 text-xs text-content/50">
          <Users className="h-3 w-3" />
          <span>{t('nearMe.community')}</span>
        </div>
      </div>
    </Link>
  );
}

export default NearMe;
